import { z } from 'zod';
import { uuid } from './common.js';

/**
 * Live trip sharing (spec 15). Model & privacy (binding):
 *  - The id (uuid v4) is a capability to VIEW only — anyone with the link can
 *    read, no viewer accounts, no auth cookies on the public GET.
 *  - Writing (posting positions, ending) requires a separate `writeKey` known
 *    only to the sharing device. The server stores only `sha256(writeKey)`.
 *  - Ending or expiry deletes the position rows — retention is the session
 *    row only, nothing more.
 */

export const SHARE_TTL_HOURS_DEFAULT = 24;
export const SHARE_TTL_HOURS_MAX = 24 * 7;
export const SHARE_POINTS_MAX_PER_CALL = 120;
export const SHARE_TRACK_MAX_POINTS = 500;

export const shareCreateRequest = z.object({
  boatName: z.string().trim().min(1).max(60).optional(),
  ttlHours: z.number().int().min(1).max(SHARE_TTL_HOURS_MAX).optional(),
});
export type ShareCreateRequest = z.infer<typeof shareCreateRequest>;

export const shareCreateResponse = z.object({
  id: uuid,
  writeKey: z.string(),
  expiresAt: z.number().int().nonnegative(),
});
export type ShareCreateResponse = z.infer<typeof shareCreateResponse>;

/** A position fix as posted by the sharing device. */
export const sharePointIn = z.object({
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  ts: z.number().int().nonnegative(),
  sogKn: z.number().min(0).max(150).nullable().optional(),
  cogDeg: z.number().min(0).max(360).nullable().optional(),
});
export type SharePointIn = z.infer<typeof sharePointIn>;

export const sharePointsRequest = z.object({
  writeKey: z.string().min(1),
  points: z.array(sharePointIn).min(1).max(SHARE_POINTS_MAX_PER_CALL),
});
export type SharePointsRequest = z.infer<typeof sharePointsRequest>;

export const shareEndRequest = z.object({
  writeKey: z.string().min(1),
});
export type ShareEndRequest = z.infer<typeof shareEndRequest>;

export const shareLatest = z.object({
  ts: z.number().int().nonnegative(),
  lat: z.number(),
  lon: z.number(),
  sogKn: z.number().nullable(),
  cogDeg: z.number().nullable(),
});
export type ShareLatest = z.infer<typeof shareLatest>;

/** Public, unauthenticated read model — never includes writeKey or userId. */
export const shareGetResponse = z.object({
  boatName: z.string().nullable(),
  startedAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),
  latest: shareLatest.nullable(),
  /** Server-simplified track (Douglas-Peucker), ≤ SHARE_TRACK_MAX_POINTS. */
  track: z.array(z.tuple([z.number(), z.number()])),
});
export type ShareGetResponse = z.infer<typeof shareGetResponse>;
